import ky, { HTTPError } from 'ky';

type ErrorType =
  | 'ApiAuthUnauthorized' // 401 Unauthenticated/Not logged in
  | 'ApiAuthForbidden' // 403 Unauthorized/No permission
  | 'ApiClientError' // 4xx Client-side errors (frontend responsibility)
  | 'ApiServerError' // 5xx Server-side errors (backend responsibility)
  | 'ApiNetworkError' // Network transmission errors
  | 'ApiUnknownError'; // Unknown errors

/**
 * @description Custom API error class
 */
class ApiError extends Error {
  public readonly type: ErrorType;
  public readonly status: number;
  public readonly data: any;

  constructor({
    message = 'An unknown API error occurred',
    type,
    status = 0,
    data = null,
    cause = null,
  }: {
    message?: string;
    type: ErrorType;
    status?: number;
    data?: any;
    cause?: Error | null;
  }) {
    // Call parent constructor
    super(message);

    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.data = data;

    // Critical: Manually concatenate call stack to ensure Sentry and other tools can trace to original error location
    if (cause && cause.stack) {
      this.stack = `${this.stack}\n\nCaused by:\n${cause.stack}`;
    }
  }

  // Type checking getter properties for convenience
  get isAuthError(): boolean {
    return (
      this.type === 'ApiAuthUnauthorized' || this.type === 'ApiAuthForbidden'
    );
  }

  get isServerError(): boolean {
    return this.type === 'ApiServerError';
  }

  get isClientError(): boolean {
    return this.type === 'ApiClientError';
  }

  get isNetworkError(): boolean {
    return this.type === 'ApiNetworkError';
  }

  /**
   * Static factory method: Create ApiError from HTTP response
   */
  static async fromHttpResponse(
    response: Response,
    originalError: HTTPError
  ): Promise<ApiError> {
    const status = response.status;
    let errorType: ErrorType = 'ApiUnknownError';

    // Use hierarchical error type classification
    if (status >= 500) {
      errorType = 'ApiServerError';
    } else if (status === 401) {
      errorType = 'ApiAuthUnauthorized';
    } else if (status === 403) {
      errorType = 'ApiAuthForbidden';
    } else if (status >= 400) {
      errorType = 'ApiClientError';
    }

    const contentType = response.headers.get('content-type') || '';
    let data = null;
    let message = `Request failed with status ${status}`;

    try {
      if (contentType.includes('application/json')) {
        data = await response.json();
        // Prioritize using the message field returned by the backend
        if (data && typeof data.message === 'string' && data.message) {
          message = data.message;
        }
      } else {
        // For non-JSON responses, read as text and use as error message
        message = await response.text();
      }
    } catch {
      // If parsing fails, fallback to statusText
      message = response.statusText || message;
    }

    return new ApiError({
      message,
      type: errorType,
      status,
      data,
      cause: originalError,
    });
  }

  /**
   * Static factory method: Create ApiError from network error
   */
  static fromNetworkError(originalError: Error): ApiError {
    const message = 'Request failed, please check your network connection';

    return new ApiError({
      message,
      type: 'ApiNetworkError',
      cause: originalError,
    });
  }
}

const api = ky.create({
  prefixUrl: '/',
  credentials: 'include',
  timeout: 1000 * 60,
  hooks: {
    beforeError: [
      error => {
        const { response, request } = error;
        if (
          (response.status === 401 || response.status === 403) &&
          !request.url.includes('/enterprise/admin/auth_data/')
        ) {
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
        }
        return error;
      },
    ],
  },
});

const sanitizeUrl = (url: string) => (url.startsWith('/') ? url.slice(1) : url);

// Unified error handling function
const handleError = async (error: unknown) => {
  if (error instanceof HTTPError) {
    const { response } = error;
    if (response) {
      // Scenario 1: Server has response, but status code indicates failure (4xx, 5xx)
      const apiError = await ApiError.fromHttpResponse(response, error);
      return Promise.reject(apiError);
    }
  }

  // Scenario 2: Server has no response or other errors, network-level issues
  const apiError = ApiError.fromNetworkError(
    error instanceof Error ? error : new Error('Unknown error')
  );
  return Promise.reject(apiError);
};

const callPostApi = async <T = unknown>(url: string, params = {}) => {
  const sanitizedUrl = sanitizeUrl(url);

  try {
    const response: T = await api.post(sanitizedUrl, { json: params }).json();
    return response;
  } catch (error) {
    return handleError(error);
  }
};

const callGetApi = async <T = unknown>(
  url: string,
  params?: Record<string, string | number>
) => {
  const sanitizedUrl = sanitizeUrl(url);
  try {
    const response: T = await api
      .get<T>(sanitizedUrl, { searchParams: params })
      .json();
    return response;
  } catch (error) {
    return handleError(error);
  }
};

const callPostFormApi = async <T = unknown>(
  url: string,
  formData?: FormData
) => {
  const sanitizedUrl = sanitizeUrl(url);
  try {
    const response: T = await api.post(sanitizedUrl, { body: formData }).json();
    return response;
  } catch (error) {
    return handleError(error);
  }
};

const callPutS3Api = async (url: string, file: File) => {
  return ky.put(url, {
    body: file,
    headers: {
      'content-type': file.type,
    },
  });
};

export { callPostApi, callGetApi, callPostFormApi, callPutS3Api, ApiError };

// Export types for external usage
export type { ErrorType };
